import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import i18n from 'i18next';
import {
  TbAlertTriangle,
  TbCalculator,
  TbChartLine,
  TbClock,
  TbInfoCircle,
  TbSquareCheck,
  TbCurrencyDollar,
} from 'react-icons/tb';
import wellIcon from 'src/assets/icons/well.svg';
import { SideBar, Badge, Spacer } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { routes } from 'views/navigation/routes/routes';
import { isActiveRoute, generatePath, navigateToPath } from 'store/navigation/navigation';
import { getOverviewStats } from 'store/entities/projects/projects';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

const ProjectSideBar = ({ sections, navigateToPath, getOverviewStats, overview }) => {
  const { company, project } = useParams();
  const { pathname } = useLocation();
  useEffect(() => {
    getOverviewStats(project);
  }, [project]);
  useEffect(() => {
    if (overview?.projectId) {
      sessionStorage.setItem(
        'activeProject',
        JSON.stringify({
          projectId: overview.projectId,
          name: overview.name,
          companyId: company,
        }),
      );
      sessionStorage.setItem(
        'selectedProject',
        JSON.stringify({
          name: overview.name,
          countryId: overview.country.countryId,
          fieldId: overview.field.fieldId,
          siteId: overview.site.siteId,
          wellId: overview.well.wellId,
          wellboreId: overview.wellbore.wellboreId,
          designId: overview.design.designId,
          estimateId: overview.projectId,
        }),
      );
      window.dispatchEvent(new Event('storage'));
    }
  }, [overview]);

  return (
    <SideBar
      top={48}
      options={{
        title: '',
        sections: sections.map((section, index) => ({
          ...section,
          items: section.items.map((item) => ({
            ...item,
            isActive: isActiveRoute(item.itemPath, pathname),
            onClick: () =>
              navigateToPath(
                generatePath(item.itemPath, {
                  company,
                  project,
                }),
              ),
          })),
          testId: `sidebar-item-${index}`,
        })),
      }}
    />
  );
};

const mapStateToProps = ({ entities }) => {
  const sections = [
    {
      heading: '',
      items: [
        {
          label: <label data-testid="overview-label">Overview</label>,
          itemPath: routes.overview,
          icon: <TbInfoCircle />,
          value: '#',
        },
        {
          label: <label data-testid="well-details-label">Well Details</label>,
          itemPath: routes.wellConfiguration,
          icon: wellIcon,
          value: '#',
        },
      ],
    },
    {
      heading: 'Project Setup',
      items: [
        {
          label: <label data-testid="activity-model-label">Activity Model</label>,
          itemPath: routes.activityModel,
          icon: <TbSquareCheck />,
          value: '#',
        },
        {
          label: <label data-testid="cost-setup-label">Cost Setup</label>,
          itemPath: routes.costSetup,
          icon: <TbCalculator />,
          value: '#',
        },
        {
          label: <label data-testid="project-modifiers-label">Project Modifiers</label>,
          itemPath: routes.risks,
          icon: <TbAlertTriangle />,
          value: '#',
        },
      ],
    },
    {
      heading: 'Simulations',
      items: [
        {
          label: <label data-testid="result-overview-label">Result Overview</label>,
          itemPath: routes.resultOverview,
          icon: <TbChartLine />,
          value: '#',
        },
      ],
    },
    {
      heading: 'Operations tracking',
      items: [
        {
          label: <label data-testid="time-tracker-label">Time Tracker</label>,
          itemPath: routes.timeTracker,
          icon: <TbClock />,
          value: '#',
        },
        {
          label: (
            <>
              <label data-testid="cost-tracker-label">{i18n.t(translations.costTracker_costTracker)}</label>
              <Spacer width="var(--padding)" />
              <Badge title="BETA"></Badge>
            </>
          ),
          itemPath: routes.costTracker,
          icon: <TbCurrencyDollar />,
          value: '#',
        },
      ],
    },
  ];

  const { overview } = entities.projects;

  return { sections, overview };
};

const mapDispatchToProps = {
  navigateToPath,
  getOverviewStats,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(ProjectSideBar), { isSideBar: true });

export { Container as ProjectSideBar };
